import { FC, useMemo } from "react";

import { MapLibrePolygonsLayer } from "@ryde/controls";

import { AREA_POLYGONS_STYLE } from "./area-polygons-map.constants";
import { IAreaPolygonItem } from "./area-polygons-map.models";
import { buildAreaPolygonItems } from "./area-polygons-map.utils";

interface IAreaPolygonsViewerLayerProps {
  areas: IAreaPolygonItem[];
  hiddenIds?: ReadonlySet<number>;
  // The area currently being reshaped in-map: skipped here and dims the rest.
  editingId?: number | null;
  // Hover naming stays on without it; only entering shape editing needs update permission.
  onAreaClick?: (id: number) => void;
}

/**
 * The selected areas' polygons, colour-coded per area.
 *
 * Framing is not done here: the page owns it, because the map must refit when the *set* of visible areas
 * changes and stay put when one of them is picked up for editing.
 */
export const AreaPolygonsViewerLayer: FC<IAreaPolygonsViewerLayerProps> = ({
  areas,
  hiddenIds,
  editingId,
  onAreaClick,
}) => {
  const polygons = useMemo(
    () =>
      buildAreaPolygonItems({
        areas,
        hiddenIds,
        editingId,
        isClickable: Boolean(onAreaClick),
      }),
    [areas, hiddenIds, editingId, onAreaClick],
  );

  return (
    <MapLibrePolygonsLayer
      id="area-polygons"
      polygons={polygons}
      style={AREA_POLYGONS_STYLE}
      // Naming an area on hover is not an edit, so it survives read-only access.
      isHoverEnabled={typeof editingId !== "number"}
      onPolygonClick={onAreaClick}
    />
  );
};
